/**
 * ══════════════════════════════════════════════════════════════════════
 * NETUNO DESIGN SYSTEM — Global Formatters
 * ══════════════════════════════════════════════════════════════════════
 *
 * Single source of truth for data formatting across all modules.
 * Ensures consistent display of currency, percentages, indices, and dates.
 *
 * Usage:
 *   import { fmtCurrency, fmtPercent, fmtIndex, fmtDate } from '@/lib/formatters';
 */

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ─── Currency ────────────────────────────────────────────────────────

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const currencyIntFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const currencyCompactFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
});

/** Format a number as BRL currency: R$ 1.234.567,89 */
export function fmtCurrency(value: number | null | undefined): string {
  if (value == null) return 'R$ 0,00';
  return currencyFormatter.format(value);
}

/** Format a number as BRL without decimals: R$ 1.234.568 */
export function fmtCurrencyInt(value: number | null | undefined): string {
  if (value == null) return 'R$ 0';
  return currencyIntFormatter.format(value);
}

/** Format a number as compact BRL: R$ 1,2 mi */
export function fmtCurrencyCompact(value: number | null | undefined): string {
  if (value == null) return 'R$ 0';
  return currencyCompactFormatter.format(value);
}

// ─── Percentage ──────────────────────────────────────────────────────

/** Format a number as percentage with 1 decimal place: 85,3% */
export function fmtPercent(value: number | null | undefined, decimals = 1): string {
  if (value == null) return '0%';
  return `${value.toFixed(decimals).replace('.', ',')}%`;
}

// ─── Index (SPI / CPI) ──────────────────────────────────────────────

/** Format a performance index with 2 decimal places: 0,97 */
export function fmtIndex(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toFixed(2).replace('.', ',');
}

// ─── Date ────────────────────────────────────────────────────────────

/** Format a date string as dd/MM/yyyy. Returns '—' for null/invalid. */
export function fmtDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : '')), 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return dateStr;
  }
}

/** Format a date string as dd/MM/yyyy HH:mm. */
export function fmtDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch {
    return dateStr;
  }
}

// ─── Number ──────────────────────────────────────────────────────────

const numberFormatter = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 2,
});

/** Format a plain number with Brazilian locale: 1.234,56 */
export function fmtNumber(value: number | null | undefined, decimals?: number): string {
  if (value == null) return '—';
  if (decimals != null) {
    return value.toFixed(decimals).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }
  return numberFormatter.format(value);
}
